<template>
  <div class="language-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('dataDictionary.title') }}</h2>
        <div class="header-actions">
          <a-space>
            <a-button type="primary" @click="showCreateModal">
              <template #icon>
                <PlusOutlined />
              </template>
              {{ $t('dataDictionary.addLanguage') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <a-card class="search-card">
      <a-form layout="inline" :model="filters">
        <a-form-item :label="$t('dataDictionary.languageType')">
          <a-select
            v-model:value="filters.language_type"
            :placeholder="$t('dataDictionary.languageType')"
            style="width: 120px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option value="source">{{ $t('dataDictionary.sourceLanguage') }}</a-select-option>
            <a-select-option value="target">{{ $t('dataDictionary.targetLanguage') }}</a-select-option>
            <a-select-option value="both">{{ $t('dataDictionary.both') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('dataDictionary.status')">
          <a-select
            v-model:value="filters.status"
            :placeholder="$t('dataDictionary.status')"
            style="width: 100px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option :value="true">{{ $t('dataDictionary.enabled') }}</a-select-option>
            <a-select-option :value="false">{{ $t('dataDictionary.disabled') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button @click="refreshData">
              <template #icon><ReloadOutlined /></template>
              {{ $t('dataDictionary.refresh') }}
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 数据列表 -->
    <a-card>
      <a-table
        :columns="columns"
        :data-source="languages"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'language_type'">
            <a-tag :color="getLanguageTypeColor(record.language_type)">
              {{ getLanguageTypeText(record.language_type) }}
            </a-tag>
          </template>
          
          <template v-if="column.key === 'status'">
            <a-switch
              :checked="record.status"
              @change="(checked: boolean) => handleStatusChange(record, checked)"
              :loading="record.statusLoading"
            />
          </template>
          
          <template v-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="showEditModal(record)">
                {{ $t('dataDictionary.edit') }}
              </a-button>
              <a-popconfirm
                :title="$t('dataDictionary.confirmDelete')"
                :ok-text="$t('dataDictionary.ok')"
                :cancel-text="$t('dataDictionary.cancel')"
                @confirm="handleDelete(record)"
              >
                <a-button type="link" size="small" danger>
                  {{ $t('dataDictionary.delete') }}
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 创建/编辑模态框 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? $t('dataDictionary.editLanguage') : $t('dataDictionary.addLanguageTitle')"
      :confirm-loading="modalLoading"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      width="600px"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        layout="vertical"
      >
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('dataDictionary.chineseName')" name="chinese_name">
              <a-input v-model:value="formData.chinese_name" :placeholder="$t('dataDictionary.enterChineseName')" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('dataDictionary.englishName')" name="english_name">
              <a-input v-model:value="formData.english_name" :placeholder="$t('dataDictionary.enterEnglishName')" />
            </a-form-item>
          </a-col>
        </a-row>
        
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('dataDictionary.languageCode')" name="language_code">
              <a-input 
                v-model:value="formData.language_code" 
                :placeholder="$t('dataDictionary.languageCodePlaceholder')" 
                :disabled="isEdit"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('dataDictionary.microsoftCode')" name="microsoft_code">
              <a-input v-model:value="formData.microsoft_code" :placeholder="$t('dataDictionary.microsoftCodePlaceholder')" />
            </a-form-item>
          </a-col>
        </a-row>
        
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('dataDictionary.languageType')" name="language_type">
              <a-select v-model:value="formData.language_type" :placeholder="$t('dataDictionary.selectLanguageType')">
                <a-select-option value="source">{{ $t('dataDictionary.sourceLanguage') }}</a-select-option>
                <a-select-option value="target">{{ $t('dataDictionary.targetLanguage') }}</a-select-option>
                <a-select-option value="both">{{ $t('dataDictionary.both') }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('dataDictionary.status')" name="status">
              <a-switch v-model:checked="formData.status" />
              <span class="ml-2">{{ formData.status ? $t('dataDictionary.enabled') : $t('dataDictionary.disabled') }}</span>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { message } from 'ant-design-vue'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons-vue'
import type { TableColumnsType, TableProps } from 'ant-design-vue'
import { LanguageAPI, type Language, type CreateLanguageRequest, type UpdateLanguageRequest, LanguageType } from '@/modules/system/api/language'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// 响应式数据
const loading = ref(false)
const languages = ref<Language[]>([])
const modalVisible = ref(false)
const modalLoading = ref(false)
const isEdit = ref(false)
const formRef = ref()

// 筛选器
const filters = reactive({
  language_type: undefined as LanguageType | undefined,
  status: undefined as boolean | undefined
})

// 分页配置
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number) => t('common.totalRecords', { total }),
  // 使用 onChange 处理页码变化
  onChange: (page: number, pageSize: number) => {
    pagination.current = page
    pagination.pageSize = pageSize
    loadData()
  },
  // 使用 onShowSizeChange 处理每页数量变化
  onShowSizeChange: (current: number, size: number) => {
    pagination.current = current
    pagination.pageSize = size
    loadData()
  }
})

// 表单数据
const formData = reactive<CreateLanguageRequest>({
  chinese_name: '',
  english_name: '',
  language_code: '',
  microsoft_code: '',
  language_type: LanguageType.BOTH,
  status: true
})

// 表单验证规则
const formRules = computed(() => ({
  chinese_name: [
    { required: true, message: t('dataDictionary.chineseNameRequired'), trigger: 'blur' },
    { max: 100, message: t('dataDictionary.chineseNameMaxLength'), trigger: 'blur' }
  ],
  english_name: [
    { required: true, message: t('dataDictionary.englishNameRequired'), trigger: 'blur' },
    { max: 100, message: t('dataDictionary.englishNameMaxLength'), trigger: 'blur' }
  ],
  language_code: [
    { required: true, message: t('dataDictionary.languageCodeRequired'), trigger: 'blur' },
    { max: 10, message: t('dataDictionary.languageCodeMaxLength'), trigger: 'blur' },
    { pattern: /^[a-z]{2}(-[a-z]{2})?$/, message: t('dataDictionary.languageCodeFormat'), trigger: 'blur' }
  ],
  microsoft_code: [
    { required: true, message: t('dataDictionary.microsoftCodeRequired'), trigger: 'blur' },
    { max: 20, message: t('dataDictionary.microsoftCodeMaxLength'), trigger: 'blur' }
  ],
  language_type: [
    { required: true, message: t('dataDictionary.languageTypeRequired'), trigger: 'change' }
  ]
}))

// 表格列配置
const columns = computed<TableColumnsType>(() => [
  {
    title: t('dataDictionary.serialNumber'),
    key: 'index',
    width: 80,
    customRender: ({ index }) => pagination.pageSize * (pagination.current - 1) + index + 1
  },
  {
    title: t('dataDictionary.chineseName'),
    dataIndex: 'chinese_name',
    key: 'chinese_name',
    width: 120
  },
  {
    title: t('dataDictionary.englishName'),
    dataIndex: 'english_name',
    key: 'english_name',
    width: 150
  },
  {
    title: t('dataDictionary.languageCode'),
    dataIndex: 'language_code',
    key: 'language_code',
    width: 100
  },
  {
    title: t('dataDictionary.microsoftCode'),
    dataIndex: 'microsoft_code',
    key: 'microsoft_code',
    width: 100
  },
  {
    title: t('dataDictionary.languageType'),
    key: 'language_type',
    width: 120
  },
  {
    title: t('dataDictionary.status'),
    key: 'status',
    width: 80
  },
  {
    title: t('dataDictionary.createTime'),
    dataIndex: 'created_at',
    key: 'created_at',
    width: 180,
    customRender: ({ text }) => new Date(text).toLocaleString()
  },
  {
    title: t('dataDictionary.operation'),
    key: 'action',
    width: 120,
    fixed: 'right'
  }
])

// 计算属性
const getLanguageTypeColor = (type: LanguageType) => {
  const colors = {
    [LanguageType.SOURCE]: 'blue',
    [LanguageType.TARGET]: 'green',
    [LanguageType.BOTH]: 'purple'
  }
  return colors[type]
}

const getLanguageTypeText = (type: LanguageType) => {
  const texts = {
    [LanguageType.SOURCE]: t('dataDictionary.sourceLanguage'),
    [LanguageType.TARGET]: t('dataDictionary.targetLanguage'),
    [LanguageType.BOTH]: t('dataDictionary.both')
  }
  return texts[type]
}

// 方法
const loadData = async () => {
  try {
    loading.value = true
    const response = await LanguageAPI.getLanguages({
      language_type: filters.language_type,
      status: filters.status,
      page: pagination.current,
      size: pagination.pageSize
    })
    
    languages.value = response.items
    pagination.total = response.total
  } catch (error) {
    console.error('加载语言数据失败:', error)
  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  loadData()
}

const handleFilterChange = () => {
  pagination.current = 1
  loadData()
}

// 处理表格变化（排序和筛选）
// 注意：分页变化已经通过 pagination 配置的 onChange/onShowSizeChange 处理
// @change 事件会传递三个参数：(pagination, filters, sorter)
const handleTableChange: TableProps['onChange'] = (pag, filters, sorter) => {
  // 分页变化已经在 pagination 的 onChange/onShowSizeChange 中处理
  // 这里只处理排序和筛选（如果需要的话）
}

const showCreateModal = () => {
  isEdit.value = false
  resetFormData()
  modalVisible.value = true
}

const showEditModal = (record: Language) => {
  isEdit.value = true
  Object.assign(formData, {
    chinese_name: record.chinese_name,
    english_name: record.english_name,
    language_code: record.language_code,
    microsoft_code: record.microsoft_code,
    language_type: record.language_type,
    status: record.status
  })
  ;(formData as any).id = record.id
  modalVisible.value = true
}

const resetFormData = () => {
  Object.assign(formData, {
    chinese_name: '',
    english_name: '',
    language_code: '',
    microsoft_code: '',
    language_type: LanguageType.BOTH,
    status: true
  })
  delete (formData as any).id
}

const handleModalOk = async () => {
  try {
    await formRef.value.validate()
    modalLoading.value = true
    
    if (isEdit.value) {
      const updateData: UpdateLanguageRequest = { ...formData }
      const recordId = (formData as any).id
      delete (updateData as any).id
      await LanguageAPI.updateLanguage(recordId, updateData)
      message.success(t('dataDictionary.updateSuccess'))
    } else {
      await LanguageAPI.createLanguage(formData)
      message.success(t('dataDictionary.createSuccess'))
    }
    
    modalVisible.value = false
    loadData()
  } catch (error) {
    console.error('保存语言配置失败:', error)
  } finally {
    modalLoading.value = false
  }
}

const handleModalCancel = () => {
  modalVisible.value = false
  formRef.value?.resetFields()
}

const handleStatusChange = async (record: Language, checked: boolean) => {
  try {
    record.statusLoading = true
    await LanguageAPI.updateLanguage(record.id, { status: checked })
    record.status = checked
    message.success(checked ? t('dataDictionary.enableSuccess') : t('dataDictionary.disableSuccess'))
  } catch (error) {
    console.error('更新状态失败:', error)
  } finally {
    record.statusLoading = false
  }
}

const handleDelete = async (record: Language) => {
  try {
    await LanguageAPI.deleteLanguage(record.id)
    message.success(t('dataDictionary.deleteSuccess'))
    loadData()
  } catch (error) {
    console.error('删除语言配置失败:', error)
  }
}

// 生命周期
onMounted(() => {
  loadData()
})
</script>

<style scoped lang="less">
.language-management {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .header-actions {
      flex-shrink: 0;
    }
  }
}

.search-card {
  margin-bottom: 24px;
}

.ml-2 {
  margin-left: 8px;
}
</style>